import React, { useState, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ConversationSidebar } from '@/components/conversation/ConversationSidebar';
import { useConversation } from '@/contexts/ConversationContext';
import { celebrationService } from '@/lib/api/celebration.service';
import { eventService } from '@/lib/api/event.service';

interface PersistentAIAssistantProps {
  onEventCreated?: (eventData: unknown) => void;
}

interface AssistantPageContext {
  currentPage?: string;
  pageType?: string;
  celebrationId?: number;
  celebrationTitle?: string;
  celebrationType?: string;
  celebrationDescription?: string;
  eventId?: number;
  eventName?: string;
  eventType?: string;
  eventDescription?: string;
  eventDate?: string;
}

export const PersistentAIAssistant: React.FC<PersistentAIAssistantProps> = ({ 
  onEventCreated 
}) => {
  const { isConversationVisible, showConversationBubble, openConversation, closeConversation } = useConversation();
  const [currentContext, setCurrentContext] = useState<AssistantPageContext>({});
  const location = useLocation();
  const params = useParams();

  // Extract context from current route
  useEffect(() => {
    const extractContext = async () => {
      const pathname = location.pathname;
      const context: AssistantPageContext = {
        currentPage: pathname,
        pageType: 'general'
      };

      // Extract celebration context
      if (pathname.includes('/celebrations/')) {
        const celebrationIdMatch = pathname.match(/\/celebrations\/(\d+)/);
        if (celebrationIdMatch) {
          const celebrationId = parseInt(celebrationIdMatch[1]);
          context.celebrationId = celebrationId;
          context.pageType = 'celebration';
          
          try {
            const celebration = await celebrationService.getCelebration(celebrationId);
            context.celebrationTitle = celebration.title;
            context.celebrationType = celebration.celebration_type;
            context.celebrationDescription = celebration.description;
          } catch (error) {
            console.error('Failed to fetch celebration details for context:', error);
          }

          // Determine specific celebration page
          if (pathname.includes('/events')) {
            context.pageType = 'celebration-events';
          } else if (pathname.includes('/guests')) {
            context.pageType = 'celebration-guests';
          } else if (pathname.includes('/tasks')) {
            context.pageType = 'celebration-tasks';
          } else if (pathname.includes('/services')) {
            context.pageType = 'celebration-services';
          } else if (pathname.includes('/settings')) {
            context.pageType = 'celebration-settings';
          } else {
            context.pageType = 'celebration-details';
          }
        }
      }

      // Extract event context
      if (pathname.includes('/events/') && !pathname.includes('/celebrations/')) {
        const eventIdMatch = pathname.match(/\/events\/(\d+)/);
        if (eventIdMatch) {
          const eventId = parseInt(eventIdMatch[1]);
          context.eventId = eventId;
          context.pageType = 'event';
          
          try {
            const event = await eventService.getEvent(eventId);
            context.eventName = event.title;
            context.eventType = event.event_type;
            context.eventDescription = event.description;
            context.eventDate = event.event_date;
          } catch (error) {
            console.error('Failed to fetch event details for context:', error);
          }
        }
      }

      // Set page-specific context
      if (pathname === '/events') {
        context.pageType = 'all-events';
      } else if (pathname === '/guests') {
        context.pageType = 'all-guests';
      } else if (pathname === '/reports') {
        context.pageType = 'reports';
      } else if (pathname === '/celebrations/new' || pathname === '/quick' || pathname === '/celebrations/create') {
        context.pageType = 'create-celebration';
      } else if (pathname.includes('/events/new')) {
        context.pageType = 'create-event';
      }

      setCurrentContext(context);
    };

    extractContext();
  }, [location.pathname, params]);

  // Don't render on the full-screen chat creation page (it manages its own layout)
  // or on the celebration list page which has its own sidebar.
  // Form-based creation routes (/quick, /celebrations/create, /celebrations/new?mode=form)
  // now ALLOW the sidebar so users get AI assistance while filling the form.
  const usesOwnConversation =
    location.pathname === '/celebrations' ||
    (location.pathname.includes('/celebrations/new') &&
      new URLSearchParams(location.search).get('mode') === 'chat') ||
    (location.pathname.includes('/celebrations/') && location.pathname.includes('/edit'));

  if (usesOwnConversation) {
    return null;
  }

  return (
    <>
      {/* AI Assistant Sidebar - positioned below both ribbons with rounded corners */}
      <div className={`
        fixed left-2 bg-white border border-gray-200 shadow-lg z-50 transition-all duration-300 ease-in-out
        rounded-xl overflow-hidden
        ${isConversationVisible ? 'w-80 opacity-100' : 'w-0 opacity-0 border-0'}
      `}
      style={{
        top: '48px', // Header height
        height: 'calc(100vh - 48px)', // Full height minus header
      }}
      data-testid="ai-assistant-sidebar"
      >
        {/* Content - Only show when expanded */}
        {isConversationVisible && (
          <div className="flex flex-col h-full w-80" data-testid="ai-assistant-content">
            {/* Conversation Content */}
            <div className="flex-1 overflow-hidden">
              <ConversationSidebar
                onEventCreated={onEventCreated}
                pageContext={currentContext}
                onClose={closeConversation}
              />
            </div>
          </div>
        )}
      </div>

      {/* Collapsed chat button - separate from sidebar */}
      {showConversationBubble && (
        <div className="fixed left-4 bottom-4 z-50" data-testid="ai-assistant-toggle-container">
          <Button
            onClick={openConversation}
            size="lg"
            title="Open AI Assistant"
            data-testid="ai-assistant-toggle-button"
          >
            <MessageCircle className="h-6 w-6" />
          </Button>
        </div>
      )}
    </>
  );
};
